export type DashboardDatePreset = 'today' | 'last7' | 'month'

export const DASHBOARD_WEEKDAY_LABELS = [
  { key: 'dom', label: 'D' },
  { key: 'seg', label: 'S' },
  { key: 'ter', label: 'T' },
  { key: 'qua', label: 'Q' },
  { key: 'qui', label: 'Q' },
  { key: 'sex', label: 'S' },
  { key: 'sab', label: 'S' },
] as const

export function parseIsoDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function toIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function formatDashboardRangeLabel(from: string, to: string, compact = false): string {
  const options: Intl.DateTimeFormatOptions = compact
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'long', year: 'numeric' }

  const start = parseIsoDate(from).toLocaleDateString('pt-BR', options)
  const end = parseIsoDate(to).toLocaleDateString('pt-BR', options)
  return `${start} - ${end}`
}

export function formatMonthTitle(year: number, month: number): string {
  const label = new Date(year, month, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export function isDateInRange(iso: string, from: string, to: string): boolean {
  return iso >= from && iso <= to
}

export function buildMonthCells(viewYear: number, viewMonth: number): Array<{ iso: string; inMonth: boolean }> {
  const first = new Date(viewYear, viewMonth, 1)
  const startOffset = first.getDay()
  const cursor = new Date(viewYear, viewMonth, 1 - startOffset)
  const cells: Array<{ iso: string; inMonth: boolean }> = []

  for (let index = 0; index < 42; index += 1) {
    cells.push({
      iso: toIsoDate(cursor),
      inMonth: cursor.getMonth() === viewMonth,
    })
    cursor.setDate(cursor.getDate() + 1)
  }

  return cells
}

export function detectPreset(from: string, to: string): DashboardDatePreset | null {
  const today = toIsoDate(new Date())
  if (from === today && to === today) return 'today'

  const last7Start = new Date()
  last7Start.setDate(last7Start.getDate() - 6)
  if (from === toIsoDate(last7Start) && to === today) return 'last7'

  const monthStart = new Date()
  monthStart.setDate(1)
  if (from === toIsoDate(monthStart) && to === today) return 'month'

  return null
}

export function getPresetRange(preset: DashboardDatePreset): { from: string; to: string } {
  const end = new Date()
  const today = toIsoDate(end)

  if (preset === 'today') {
    return { from: today, to: today }
  }

  if (preset === 'last7') {
    const start = new Date(end)
    start.setDate(start.getDate() - 6)
    return { from: toIsoDate(start), to: today }
  }

  const start = new Date(end.getFullYear(), end.getMonth(), 1)
  return { from: toIsoDate(start), to: today }
}

export function defaultDashboardPeriodRange(): { from: string; to: string } {
  return getPresetRange('month')
}

export function normalizeRange(from: string, to: string): { from: string; to: string } {
  if (!to || to < from) return { from, to: from }
  return { from, to }
}

export function selectRangeDay(currentFrom: string, currentTo: string, iso: string): { from: string; to: string } {
  if (!currentFrom || (currentFrom && currentTo)) {
    return { from: iso, to: '' }
  }

  if (iso < currentFrom) {
    return { from: iso, to: currentFrom }
  }

  return { from: currentFrom, to: iso }
}
